import React from 'react';
import {
  Mail,
  Building,
  ShieldCheck,
  ShieldAlert,
  Clock,
  Check,
  CheckCircle2,
  Copy,
  Calendar,
  Hash,
  FileText,
  Phone,
  MapPin,
  Globe,
  Share2,
  ExternalLink,
  Award,
} from 'lucide-react';
import type { User } from '../../../../shared/types/index.js';
import type { RoleBadgeConfig } from './profileConstants.js';

interface ProfileDetailsTabProps {
  user: User;
  roleBadgeInfo: RoleBadgeConfig;
  copiedId: boolean;
  onCopyId: () => void;
  onNavigateToName: () => void;
  onNavigateToPassword: () => void;
}

export const ProfileDetailsTab: React.FC<ProfileDetailsTabProps> = ({
  user,
  roleBadgeInfo,
  copiedId,
  onCopyId,
  onNavigateToName,
  onNavigateToPassword,
}) => {
  const [copiedLink, setCopiedLink] = React.useState(false);

  const handleCopyLink = () => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://uibn.id';
    const link = `${origin}/${user.role === 'penerbit' ? 'penerbit' : 'penulis'}/${encodeURIComponent(user.organization || user.name)}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2500);
  };

  const isPubVerified = Boolean(
    user.isPublisherVerified === 1 ||
    user.isPublisherVerified === true ||
    user.publisherVerificationStatus === 'verified'
  );
  const isPubPending = user.publisherVerificationStatus === 'pending';
  const isPubRejected = user.publisherVerificationStatus === 'rejected';

  return (
    <div className="space-y-6 animate-in fade-in duration-200">
      <div>
        <h3 className="text-base font-bold text-slate-900">Informasi & Kredensial Akun</h3>
        <p className="text-xs text-slate-500 mt-0.5">
          Rincian identitas registrasi dan hak akses peran yang aktif dalam sistem UIBN.
        </p>
      </div>

      {/* Public Showcase Share Card */}
      <div className="p-4 sm:p-5 rounded-2xl bg-gradient-to-r from-indigo-950 via-slate-900 to-slate-950 text-white shadow-md flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border border-indigo-900/60">
        <div className="space-y-1.5 max-w-md">
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 rounded-md bg-indigo-500/20 text-indigo-300 font-bold text-[10px] uppercase border border-indigo-400/30">
              Halaman Publik Resmi
            </span>
          </div>
          <h4 className="font-bold text-sm text-white">
            Katalog Publik Buku Terbit {user.role === 'organisasi' ? 'Organisasi & Komunitas' : user.role === 'penerbit' ? 'Penerbit' : 'Penulis'}
          </h4>
          <p className="text-xs text-slate-300/90 leading-relaxed">
            Bagikan tautan halaman publik Anda untuk menampilkan semua naskah berkode UIBN yang telah disetujui secara publik.
          </p>
        </div>

        <div className="flex items-center gap-2.5 w-full md:w-auto shrink-0">
          <button
            type="button"
            onClick={handleCopyLink}
            className="flex-1 md:flex-initial h-10 px-3 sm:px-4 py-2 rounded-xl bg-slate-800/80 hover:bg-slate-800 text-white font-bold text-xs transition flex items-center justify-center gap-1.5 shadow-2xs border border-slate-700/60 cursor-pointer min-w-0 text-center"
          >
            {copiedLink ? (
              <>
                <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                <span className="text-emerald-400 truncate">Tautan Disalin</span>
              </>
            ) : (
              <>
                <Copy className="w-4 h-4 text-indigo-400 shrink-0" />
                <span className="truncate">Salin Tautan</span>
              </>
            )}
          </button>

          <a
            href={`/${user.role === 'organisasi' ? 'organisasi' : user.role === 'penerbit' ? 'penerbit' : 'penulis'}/${encodeURIComponent(user.organization || user.name)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 md:flex-initial h-10 px-3 sm:px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs transition flex items-center justify-center gap-1.5 shadow-xs cursor-pointer min-w-0 text-center"
          >
            <Share2 className="w-4 h-4 text-indigo-300 shrink-0" />
            <span className="truncate">Buka Halaman</span>
            <ExternalLink className="w-3.5 h-3.5 opacity-80 shrink-0" />
          </a>
        </div>
      </div>

      {/* Verified Publisher Data Card for Publisher & Organization Accounts */}
      {(user.role === 'penerbit' || user.role === 'organisasi') && (
        <div
          className={`p-5 rounded-3xl border transition shadow-sm space-y-4 ${
            isPubVerified
              ? 'bg-gradient-to-br from-emerald-50/90 via-teal-50/30 to-slate-50 border-emerald-200/90'
              : isPubPending
              ? 'bg-gradient-to-br from-amber-50/90 via-orange-50/30 to-slate-50 border-amber-200/90'
              : isPubRejected
              ? 'bg-gradient-to-br from-rose-50/90 via-red-50/30 to-slate-50 border-rose-200/90'
              : 'bg-purple-50/60 border-purple-200'
          }`}
        >
          {/* Header Row */}
          <div className="flex items-start sm:items-center justify-between flex-wrap gap-3 pb-3 border-b border-slate-200/80">
            <div className="flex items-center gap-3">
              <div
                className={`w-10 h-10 rounded-2xl flex items-center justify-center shadow-xs shrink-0 ${
                  isPubVerified
                    ? 'bg-emerald-600 text-white'
                    : isPubPending
                    ? 'bg-amber-600 text-white'
                    : isPubRejected
                    ? 'bg-rose-600 text-white'
                    : 'bg-purple-700 text-white'
                }`}
              >
                {isPubVerified ? (
                  <ShieldCheck className="w-5 h-5" />
                ) : isPubPending ? (
                  <Clock className="w-5 h-5" />
                ) : isPubRejected ? (
                  <ShieldAlert className="w-5 h-5" />
                ) : (
                  <Building className="w-5 h-5" />
                )}
              </div>
              <div>
                <h4 className="font-bold text-slate-900 text-sm sm:text-base flex items-center gap-2 flex-wrap">
                  <span>{user.role === 'organisasi' ? 'Data Legalitas & Profil Komunitas' : 'Data Legalitas & Verifikasi Penerbit'}</span>
                  {isPubVerified && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-extrabold uppercase border border-emerald-300">
                      <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                      Disetujui Resmi
                    </span>
                  )}
                </h4>
                <p className="text-xs text-slate-500 mt-0.5">
                  {isPubVerified
                    ? 'Data identitas lembaga dan dokumen legalitas resmi yang telah divalidasi sah oleh Admin UIBN.'
                    : isPubPending
                    ? 'Data legalitas sedang dalam antrean peninjauan oleh tim verifikator UIBN.'
                    : isPubRejected
                    ? 'Permohonan verifikasi perlu perbaikan sesuai catatan admin di bawah.'
                    : 'Lengkapi identitas lembaga saat pertama kali mendaftarkan buku untuk diverifikasi.'}
                </p>
              </div>
            </div>

            {/* Status Badge */}
            {isPubVerified ? (
              <span className="px-3 py-1.5 rounded-xl bg-emerald-600 text-white font-bold text-xs flex items-center gap-1.5 shadow-xs">
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span>Terverifikasi Resmi</span>
              </span>
            ) : isPubPending ? (
              <span className="px-3 py-1.5 rounded-xl bg-amber-500 text-white font-bold text-xs flex items-center gap-1.5 shadow-xs">
                <Clock className="w-3.5 h-3.5" />
                <span>Menunggu Validasi</span>
              </span>
            ) : isPubRejected ? (
              <span className="px-3 py-1.5 rounded-xl bg-rose-600 text-white font-bold text-xs flex items-center gap-1.5 shadow-xs">
                <ShieldAlert className="w-3.5 h-3.5" />
                <span>Perlu Revisi</span>
              </span>
            ) : (
              <span className="px-3 py-1.5 rounded-xl bg-slate-200 text-slate-700 font-bold text-xs flex items-center gap-1.5">
                <ShieldAlert className="w-3.5 h-3.5 text-slate-500" />
                <span>Belum Terverifikasi</span>
              </span>
            )}
          </div>

          {/* Admin Notes / Remarks Box */}
          {user.publisherVerificationNotes && (
            <div
              className={`p-3.5 rounded-2xl border text-xs ${
                isPubVerified
                  ? 'bg-emerald-100/70 border-emerald-200 text-emerald-950'
                  : isPubRejected
                  ? 'bg-rose-100/70 border-rose-200 text-rose-950'
                  : 'bg-white/90 border-slate-200 text-slate-800'
              }`}
            >
              <div className="flex items-start gap-2.5">
                <FileText
                  className={`w-4 h-4 shrink-0 mt-0.5 ${
                    isPubVerified
                      ? 'text-emerald-700'
                      : isPubRejected
                      ? 'text-rose-700'
                      : 'text-slate-500'
                  }`}
                />
                <div className="space-y-0.5 flex-1">
                  <span className="font-bold block text-slate-900">
                    {isPubVerified
                      ? 'Catatan & Keputusan Admin UIBN:'
                      : isPubRejected
                      ? 'Catatan Perbaikan dari Admin UIBN:'
                      : 'Catatan Verifikasi:'}
                  </span>
                  <p className="leading-relaxed font-medium">
                    "{user.publisherVerificationNotes}"
                  </p>
                  {isPubVerified && user.publisherVerifiedAt && (
                    <span className="block text-[10px] text-emerald-800 font-medium pt-1">
                      Tanggal Diverifikasi:{' '}
                      {new Date(user.publisherVerifiedAt).toLocaleDateString('id-ID', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                      })}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Data Grid: All input fields submitted during verification */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-xs">
            {/* Nama Legal */}
            <div className="bg-white/90 p-3.5 rounded-2xl border border-slate-200/90 shadow-2xs space-y-1">
              <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider flex items-center gap-1.5">
                <Building className="w-3.5 h-3.5 text-indigo-500" />
                Nama Badan Usaha / Legal
              </span>
              <p className="font-bold text-slate-900 text-xs sm:text-sm">
                {user.publisherLegalName || user.organization || user.name || '-'}
              </p>
            </div>

            {/* NIB / Legalitas */}
            <div className="bg-white/90 p-3.5 rounded-2xl border border-slate-200/90 shadow-2xs space-y-1">
              <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5 text-indigo-500" />
                Nomor Induk Berusaha (NIB) / SK
              </span>
              <p className="font-mono font-bold text-slate-900 text-xs sm:text-sm">
                {user.publisherNib || '-'}
              </p>
            </div>

            {/* Kode Penerbit UIBN */}
            <div className="bg-white/90 p-3.5 rounded-2xl border border-slate-200/90 shadow-2xs space-y-1">
              <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider flex items-center gap-1.5">
                <Hash className="w-3.5 h-3.5 text-indigo-500" />
                Kode Penerbit UIBN
              </span>
              <div className="flex items-center gap-2">
                <span className="font-mono font-extrabold text-purple-800 text-xs sm:text-sm bg-purple-50 px-2 py-0.5 rounded-lg border border-purple-200">
                  {user.publisherCode || 'Menunggu Verifikasi'}
                </span>
                {isPubVerified && (
                  <span className="text-[10px] font-bold text-emerald-600">✓ Sah</span>
                )}
              </div>
            </div>

            {/* Kontak Telepon / WA */}
            <div className="bg-white/90 p-3.5 rounded-2xl border border-slate-200/90 shadow-2xs space-y-1">
              <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5 text-indigo-500" />
                Kontak Redaksi / Telepon
              </span>
              <p className="font-semibold text-slate-900 text-xs sm:text-sm">
                {user.publisherPhone || '-'}
              </p>
            </div>

            {/* Website / Media Sosial */}
            <div className="bg-white/90 p-3.5 rounded-2xl border border-slate-200/90 shadow-2xs space-y-1">
              <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider flex items-center gap-1.5">
                <Globe className="w-3.5 h-3.5 text-indigo-500" />
                Website / Media Sosial
              </span>
              {user.publisherWebsite ? (
                <a
                  href={
                    user.publisherWebsite.startsWith('http')
                      ? user.publisherWebsite
                      : `https://${user.publisherWebsite}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 truncate text-xs transition"
                >
                  <span className="truncate">
                    {user.publisherWebsite.replace(/^https?:\/\//, '')}
                  </span>
                  <ExternalLink className="w-3 h-3 shrink-0 opacity-70" />
                </a>
              ) : (
                <p className="text-slate-400 font-medium">-</p>
              )}
            </div>

            {/* Alamat Lengkap Kantor Operasional */}
            <div className="bg-white/90 p-3.5 rounded-2xl border border-slate-200/90 shadow-2xs space-y-1 sm:col-span-2 lg:col-span-1">
              <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-indigo-500" />
                Alamat Kantor Operasional
              </span>
              <p
                className="font-medium text-slate-800 text-xs line-clamp-2"
                title={user.publisherAddress || ''}
              >
                {user.publisherAddress || '-'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Account Info Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* User ID */}
        <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200/80 flex flex-col justify-between">
          <div>
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
              <Hash className="w-3.5 h-3.5 text-slate-400" />
              ID Pengguna (Sistem)
            </span>
            <div className="flex items-center justify-between mt-2">
              <code className="text-xs font-mono font-semibold text-slate-800 bg-white px-2 py-1 rounded-md border border-slate-200">
                {user.id}
              </code>
              <button
                type="button"
                onClick={onCopyId}
                className="text-xs text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1 transition cursor-pointer"
              >
                {copiedId ? (
                  <>
                    <Check className="w-3.5 h-3.5 text-emerald-600" />
                    <span className="text-emerald-600">Disalin</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5" />
                    <span>Salin</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Email Address */}
        <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200/80">
          <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
            <Mail className="w-3.5 h-3.5 text-slate-400" />
            Alamat Email
          </span>
          <p className="text-sm font-semibold text-slate-800 mt-2 truncate">
            {user.email}
          </p>
          <span className="inline-block mt-1 text-[11px] text-emerald-600 font-medium">
            ✓ Terverifikasi resmi
          </span>
        </div>

        {/* Peran / Role Details */}
        <div className={`p-4 rounded-2xl border ${roleBadgeInfo.bg} ${roleBadgeInfo.border}`}>
          <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-slate-400" />
            Peran & Hak Akses
          </span>
          <p className={`text-sm font-bold mt-2 ${roleBadgeInfo.text}`}>
            {roleBadgeInfo.label}
          </p>
          <p className="text-xs text-slate-600 mt-1 leading-relaxed">
            {roleBadgeInfo.desc}
          </p>
        </div>

        {/* Publisher Code or Organization */}
        <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200/80">
          <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
            <Building className="w-3.5 h-3.5 text-slate-400" />
            Institusi / Organisasi
          </span>
          <p className="text-sm font-semibold text-slate-800 mt-2">
            {user.organization || user.name || 'Penulis Independen'}
          </p>
          {user.publisherCode && (
            <p className="text-xs text-purple-700 font-mono mt-1">
              Kode Penerbit UIBN: <strong>{user.publisherCode}</strong>
            </p>
          )}
        </div>
      </div>

      {/* Account Metadata footer card */}
      <div className="p-4 rounded-2xl border border-slate-200 bg-slate-50/50 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs text-slate-600">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-slate-400" />
          <span>
            Terdaftar sejak:{' '}
            <strong>
              {user.createdAt
                ? new Date(user.createdAt).toLocaleDateString('id-ID', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })
                : 'Aktif 2026'}
            </strong>
          </span>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onNavigateToName}
            className="font-semibold text-indigo-600 hover:text-indigo-800 transition cursor-pointer"
          >
            Ubah Nama &rarr;
          </button>
          <button
            type="button"
            onClick={onNavigateToPassword}
            className="font-semibold text-indigo-600 hover:text-indigo-800 transition cursor-pointer"
          >
            Ubah Kata Sandi &rarr;
          </button>
        </div>
      </div>
    </div>
  );
};

